import { MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { useTranslate } from "@tolgee/react";
import { dismissItemHint } from "../../../doc-items/ItemDetailHintBar.js";
import { SaveTemplateDialog } from "../SaveTemplateDialog.js";
import { SAVE_AS_TEMPLATE_HINT_KEY } from "../SaveAsTemplateHintBar.js";

interface Props {
  projectId: string | null;
  docId: string;
  /** The item whose content the dialog shows. */
  sourceItemId: string;
  /** `null` saves the whole quote — the backend then uses its root item. */
  originItemId: string | null;
  sourceTitle: string;
  refetchQueries?: string[];
}

/**
 * The quote editor's "Save as quote template" entry. Same entry and position as
 * before; it now opens the metadata dialog instead of firing the mutation
 * straight away. The template editor's own call sites keep the direct behaviour
 * (`QuoteTemplateCreateFromItemButton`).
 */
export const QuoteTemplateSaveFromItemMenuItem = ({
  projectId,
  docId,
  sourceItemId,
  originItemId,
  sourceTitle,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate("QuoteItem");

  return (
    <ModalOpenButton
      Modal={SaveTemplateDialog}
      modalProps={{
        projectId,
        docId,
        sourceItemId,
        originItemId,
        sourceTitle,
        mode: { type: "create" as const },
        refetchQueries,
        // The item-detail hint offers this same save. Whichever entry point the
        // user took, the template now exists, so the hint has to stop asking —
        // it is keyed on the item, which the quote-level entry does not target.
        onSaved:
          originItemId === null
            ? undefined
            : () =>
                dismissItemHint(SAVE_AS_TEMPLATE_HINT_KEY, docId, originItemId),
      }}
    >
      <MenuItemWithIcon icon={<LibraryAddIcon />}>
        {t("Save as quote template")}
      </MenuItemWithIcon>
    </ModalOpenButton>
  );
};
